// Column factories for the Knowledge Quality drawer tables.
import React from "react";
import type { Column } from "../../../components/DataTable";
import { toTitleCaseFromSnake } from "../utils";
import type { KQEvidence, KQFinding } from "./knowledgeQualityTypes";
import {
  evidenceStrengthTextTone,
  severityTone,
} from "./knowledgeQualityIntelligenceHelpers";
import { ProgressBar } from "./knowledgeQualityIntelligenceParts";
import {
  KQ_ENTITY_COL,
  KQ_EVIDENCE_COL,
  KQ_FINDING_COL,
  KQ_RELATED_CONCEPTS_MAX,
} from "./utils/constants";
import type { KQEntityRow } from "./utils/helpers";

function joinSourceLocation(list: string[] | null | undefined): string {
  return (list ?? []).join(", ");
}

function formatRelatedConcepts(concepts: string[]): {
  visible: string;
  hasMore: boolean;
} {
  const visible = concepts.slice(0, KQ_RELATED_CONCEPTS_MAX).join(" · ");
  return { visible, hasMore: concepts.length > KQ_RELATED_CONCEPTS_MAX };
}

export function getKQFindingColumns(): Column<KQFinding>[] {
  return [
    {
      key: "finding_type",
      header: KQ_FINDING_COL.finding.header,
      align: "left",
      minWidth: KQ_FINDING_COL.finding.minWidth,
      render: (row) => (
        <span className="text-[12px] font-semibold text-foreground break-words">
          {toTitleCaseFromSnake(row.finding_type)}
        </span>
      ),
    },
    {
      key: "affected_entity",
      header: KQ_FINDING_COL.entity.header,
      align: "left",
      minWidth: KQ_FINDING_COL.entity.minWidth,
      render: (row) => (
        <span className="text-[12px] text-foreground break-words">
          {row.affected_entity}
        </span>
      ),
    },
    {
      key: "description",
      header: KQ_FINDING_COL.description.header,
      align: "left",
      ellipsis: 2,
      tooltip: (row) => row.description,
      render: (row) => (
        <span className="text-[12px] text-muted-foreground break-words">
          {row.description}
        </span>
      ),
    },
    {
      key: "severity",
      header: KQ_FINDING_COL.severity.header,
      align: "center",
      width: KQ_FINDING_COL.severity.width,
      render: (row) => {
        const t = severityTone(row.severity);
        return (
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${t.bg} ${t.text}`}
          >
            {t.label}
          </span>
        );
      },
    },
    {
      key: "source",
      header: KQ_FINDING_COL.source.header,
      align: "left",
      width: KQ_FINDING_COL.source.width,
      render: (row) => (
        <span className="text-[11px] text-muted-foreground break-words">
          {joinSourceLocation(row.source_location)}
        </span>
      ),
    },
  ];
}

export function getKQEntityColumns(): Column<KQEntityRow>[] {
  return [
    {
      key: "entity",
      header: KQ_ENTITY_COL.entity.header,
      align: "left",
      minWidth: KQ_ENTITY_COL.entity.minWidth,
      render: (row) => {
        const related = formatRelatedConcepts(row.relatedConcepts);
        return (
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[12px] font-semibold text-foreground break-words">
              {row.entity}
            </span>
            {related.visible && (
              <span className="text-[10.5px] text-muted-foreground break-words">
                {related.visible}
                {related.hasMore ? " ..." : ""}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "attrs",
      header: KQ_ENTITY_COL.attrs.header,
      align: "center",
      width: KQ_ENTITY_COL.attrs.width,
      render: (row) => (
        <span className="text-[12px] tabular-nums text-foreground">
          {row.available} / {row.required}
        </span>
      ),
    },
    {
      key: "completeness",
      header: KQ_ENTITY_COL.completeness.header,
      align: "left",
      minWidth: KQ_ENTITY_COL.completeness.minWidth,
      render: (row) => <ProgressBar value={row.completeness} tone="emerald" />,
    },
    {
      key: "richness",
      header: KQ_ENTITY_COL.richness.header,
      align: "left",
      minWidth: KQ_ENTITY_COL.richness.minWidth,
      render: (row) => <ProgressBar value={row.richness} tone="indigo" />,
    },
    {
      key: "trust",
      header: KQ_ENTITY_COL.trust.header,
      align: "left",
      minWidth: KQ_ENTITY_COL.trust.minWidth,
      render: (row) => <ProgressBar value={row.trust} tone="cyan" />,
    },
    {
      key: "source",
      header: KQ_ENTITY_COL.source.header,
      align: "left",
      width: KQ_ENTITY_COL.source.width,
      render: (row) => (
        <span className="text-[11px] text-muted-foreground break-words">
          {row.source}
        </span>
      ),
    },
  ];
}

export function getKQEvidenceColumns(): Column<KQEvidence>[] {
  return [
    {
      key: "entity",
      header: KQ_EVIDENCE_COL.entity.header,
      align: "left",
      minWidth: KQ_EVIDENCE_COL.entity.minWidth,
      render: (row) => (
        <span className="text-[12px] font-semibold text-foreground break-words">
          {row.knowledge_entity}
        </span>
      ),
    },
    {
      key: "type",
      header: KQ_EVIDENCE_COL.type.header,
      align: "left",
      width: KQ_EVIDENCE_COL.type.width,
      render: (row) => (
        <span className="text-[11px] text-muted-foreground">
          {row.evidence_type}
        </span>
      ),
    },
    {
      key: "source",
      header: KQ_EVIDENCE_COL.source.header,
      align: "left",
      minWidth: KQ_EVIDENCE_COL.source.minWidth,
      render: (row) => (
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-[12px] text-foreground break-words">
            {row.evidence_source}
          </span>
          <span className="text-[10.5px] text-muted-foreground break-words">
            {row.evidence_location}
          </span>
        </div>
      ),
    },
    {
      key: "strength",
      header: KQ_EVIDENCE_COL.strength.header,
      align: "center",
      width: KQ_EVIDENCE_COL.strength.width,
      render: (row) => (
        <span
          className={`text-[12px] font-semibold ${evidenceStrengthTextTone(row.evidence_strength)}`}
        >
          {row.evidence_strength}
        </span>
      ),
    },
    {
      key: "reliability",
      header: KQ_EVIDENCE_COL.reliability.header,
      align: "center",
      width: KQ_EVIDENCE_COL.reliability.width,
      render: (row) => (
        <span className="text-[11.5px] text-foreground">
          {row.source_reliability}
        </span>
      ),
    },
    {
      key: "recency",
      header: KQ_EVIDENCE_COL.recency.header,
      align: "center",
      width: KQ_EVIDENCE_COL.recency.width,
      render: (row) => (
        <span className="text-[11.5px] text-foreground">
          {row.evidence_recency}
        </span>
      ),
    },
    {
      key: "consistency",
      header: KQ_EVIDENCE_COL.consistency.header,
      align: "center",
      width: KQ_EVIDENCE_COL.consistency.width,
      render: (row) => (
        <span className="text-[11.5px] text-foreground">
          {row.evidence_consistency}
        </span>
      ),
    },
    {
      key: "trust",
      header: KQ_EVIDENCE_COL.trust.header,
      align: "left",
      minWidth: KQ_EVIDENCE_COL.trust.minWidth,
      render: (row) => <ProgressBar value={row.trust_score} tone="cyan" />,
    },
  ];
}
